// collision.ts
// Shared wall-collision resolution used by the player and by mobs
// (chicken, spider). The whole per-frame displacement goes through
// moveWithCollisions(), which applies it in small substeps so that fast
// movement (e.g. a knockback impulse) can never jump across a thin wall
// in a single frame - that was the cause of the player clipping into
// walls when kicked.
import GameMap from './gameMap';
import Chunk from './chunk';
import { AreaData, AreaNode } from '../types';
import { pointInPolygon, closestPointOnPolygonEdges, polygonBounds, isAxisAlignedRect } from '../geometry';

export type MoveResult = {
    x: number,
    y: number,
    hitWall: boolean, // true if a wall corrected the position at any substep
    overlappingAreas: AreaData[] // non-wall areas (portal, damage, trigger, door) overlapped during the move
};

// Resolves a single point against every wall area in the 3x3 chunk
// neighborhood around it (chunks are 512px, so walls near a chunk boundary
// still block movement coming from the neighboring chunk). Non-wall areas
// that overlap are collected into `overlapping` (deduplicated) so the caller
// can react to each of them once per move, even when the move is substepped.
function resolvePoint(gameMap: GameMap, px: number, py: number, collisionOffset: number, overlapping: Set<AreaData>): { x: number, y: number, hitWall: boolean } {
    let tmpX = px;
    let tmpY = py;
    let hitWall = false;

    let currentChunkX = Math.floor(tmpX / Chunk.CHUNK_SIZE);
    let currentChunkY = Math.floor(tmpY / Chunk.CHUNK_SIZE);

    for(let cx = currentChunkX - 1; cx <= currentChunkX + 1; cx++){
    for(let cy = currentChunkY - 1; cy <= currentChunkY + 1; cy++){
        let chunk = gameMap.getChunk(cx, cy);
        if(!chunk || !chunk.data.areas){
            continue;
        }

        let chunkOffsetX = chunk.tileX * Chunk.CHUNK_SIZE;
        let chunkOffsetY = chunk.tileY * Chunk.CHUNK_SIZE;

        for(let area of chunk.data.areas){
            if(area.nodes.length < 3){
                continue; // invalid/degenerate area, shouldn't be saved but skip defensively
            }

            // Fast path: most areas (especially existing/unedited ones) are plain
            // axis-aligned rectangles. Reuse the exact rect-inflate + closest-side
            // snap logic - this matters because general polygon push-out jitters
            // on thin corridor walls (some are only 16px wide) where the two long
            // edges are very close together. Non-rectangular (hand-edited) areas
            // fall through to the general polygon path below.
            let rect = isAxisAlignedRect(area.nodes);
            let isOverlapping = false;
            let bounds = rect ? polygonBounds(area.nodes) : null;
            let worldNodes: AreaNode[] = [];
            let closest = { x: 0, y: 0, dist: 0, nx: 0, ny: 0 };

            // Corner-region circular push-out (see below) - only set when
            // the point is diagonally outside the rect's corner.
            let cornerPush: { x: number, y: number } | null = null;

            let wallX2 = 0, wallY2 = 0, wallW2 = 0, wallH2 = 0;

            if(rect && bounds){
                let rectMinX = chunkOffsetX + bounds.minX;
                let rectMaxX = chunkOffsetX + bounds.maxX;
                let rectMinY = chunkOffsetY + bounds.minY;
                let rectMaxY = chunkOffsetY + bounds.maxY;

                let inXRange = tmpX >= rectMinX && tmpX <= rectMaxX;
                let inYRange = tmpY >= rectMinY && tmpY <= rectMaxY;

                if(!inXRange && !inYRange){
                    // Diagonally outside a corner - the square Minkowski-sum
                    // inflation below over-triggers here (it has square
                    // corners, not round ones), and its single-axis snap
                    // resolves the wrong way, which is what let entities get
                    // caught on corners instead of sliding past them. Treat
                    // the mover as a circle of radius collisionOffset around
                    // the corner vertex instead, same as the general polygon
                    // path does for every vertex.
                    let cornerX = tmpX < rectMinX ? rectMinX : rectMaxX;
                    let cornerY = tmpY < rectMinY ? rectMinY : rectMaxY;
                    let dx = tmpX - cornerX;
                    let dy = tmpY - cornerY;
                    let dist = Math.sqrt(dx*dx + dy*dy);

                    isOverlapping = dist < collisionOffset;
                    if(isOverlapping){
                        if(dist < 0.0001){
                            cornerPush = { x: cornerX + collisionOffset, y: cornerY };
                        }else{
                            cornerPush = { x: cornerX + (dx/dist) * collisionOffset, y: cornerY + (dy/dist) * collisionOffset };
                        }
                    }
                }else{
                    let wallX = chunkOffsetX - tmpX + bounds.minX;
                    let wallY = chunkOffsetY - tmpY + bounds.minY;
                    let wallW = bounds.maxX - bounds.minX;
                    let wallH = bounds.maxY - bounds.minY;

                    wallX2 = wallX - collisionOffset;
                    wallY2 = wallY - collisionOffset;
                    wallW2 = wallW + collisionOffset*2;
                    wallH2 = wallH + collisionOffset*2;

                    isOverlapping = (0 > wallX2 && 0 < wallX2 + wallW2 && 0 > wallY2 && 0 < wallY2 + wallH2);
                }
            }else{
                worldNodes = area.nodes.map(n => ({x: chunkOffsetX + n.x, y: chunkOffsetY + n.y}));
                let inside = pointInPolygon(tmpX, tmpY, worldNodes);
                closest = closestPointOnPolygonEdges(tmpX, tmpY, worldNodes);
                isOverlapping = inside || closest.dist < collisionOffset;
            }

            if(isOverlapping){
                if(area.type && area.type != "wall"){
                    // portal, damage, trigger, door... - the caller decides what
                    // to do with these, they don't block movement.
                    overlapping.add(area);
                }else{ //"wall" or not specified (backwards compatibility)
                    hitWall = true;
                    if(cornerPush){
                        tmpX = cornerPush.x;
                        tmpY = cornerPush.y;
                    }else if(rect && bounds){
                        // Calculate distances to each side
                        let leftDist = Math.abs(wallX2);
                        let rightDist = Math.abs(wallX2 + wallW2);
                        let topDist = Math.abs(wallY2);
                        let bottomDist = Math.abs(wallY2 + wallH2);

                        // Find the closest side
                        let minDist = Math.min(leftDist, rightDist, topDist, bottomDist);

                        if (minDist === leftDist) {
                            // Move to the left side of the wall
                            tmpX = chunkOffsetX + bounds.minX - collisionOffset;
                        } else if (minDist === rightDist) {
                            // Move to the right side of the wall
                            tmpX = chunkOffsetX + bounds.maxX + collisionOffset;
                        } else if (minDist === topDist) {
                            // Move to the top side of the wall
                            tmpY = chunkOffsetY + bounds.minY - collisionOffset;
                        } else if (minDist === bottomDist) {
                            // Move to the bottom side of the wall
                            tmpY = chunkOffsetY + bounds.maxY + collisionOffset;
                        }
                    }else{
                        // General polygon: push out along the outward normal of the
                        // nearest boundary point by collisionOffset (treats the
                        // mover as a small circle of that radius).
                        tmpX = closest.x + closest.nx * collisionOffset;
                        tmpY = closest.y + closest.ny * collisionOffset;
                    }
                }
            }
        }
    }
    }

    return { x: tmpX, y: tmpY, hitWall };
}

// Moves from (startX, startY) by (dx, dy), resolving wall collisions along
// the way. The displacement is split into substeps smaller than
// collisionOffset, so even a large knockback impulse gets stopped by (and
// slides along) walls instead of tunneling through them.
export function moveWithCollisions(gameMap: GameMap, startX: number, startY: number, dx: number, dy: number, collisionOffset: number): MoveResult {
    let maxStep = Math.max(1, collisionOffset - 1);
    let distance = Math.sqrt(dx * dx + dy * dy);
    let steps = Math.max(1, Math.ceil(distance / maxStep));

    let x = startX;
    let y = startY;
    let hitWall = false;
    let overlapping = new Set<AreaData>();

    for(let i = 0; i < steps; i++){
        x += dx / steps;
        y += dy / steps;
        let resolved = resolvePoint(gameMap, x, y, collisionOffset, overlapping);
        x = resolved.x;
        y = resolved.y;
        hitWall = hitWall || resolved.hitWall;
    }

    return { x, y, hitWall, overlappingAreas: Array.from(overlapping) };
}
